import { Box, HStack, HTMLChakraProps, Skeleton, Stack, useBreakpointValue, useTheme, VStack } from '@chakra-ui/react';
import FullCalendar, { FormatterInput } from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { isSameDay } from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
import { FC, useEffect, useMemo, useRef } from 'react';

import { ScheduleCalendarNoTracks } from '@/components/ui/features/schedule/ScheduleCalendar/components/ScheduleCalendarNoTracks';
import { ScheduleCalendarTrackEmbedded } from '@/components/ui/features/schedule/ScheduleCalendar/components/ScheduleCalendarTrackEmbedded';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResourceRelationship, useResources } from '@/lib/datx-jsonapi-react';
import { queryThinEventSessions } from '@/queries/thin-event-sessions';
import { queryThinEvent, queryThinEventConfigurationRelationship } from '@/queries/thin-events';

import styles from './ScheduleCalendar.module.css';
import {
  getFirstEventByDate,
  getNormalizedEvents,
  getTracksGroups,
  handleEventMouseEnter,
  handleEventMouseLeave,
} from './ScheduleCalendar.utils';

import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import { EmbeddingScheduleType, EventConfiguration } from '@/resources/EventConfiguration';
import { queryAllTracks } from '@/queries/tracks';

interface IUrlQueryStringParams {
  day?: string;
  view?: string;
  eventId?: string;
  trackIds?: string;
  timeZone?: string;
}

const timeFormat: FormatterInput = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

interface IScheduleCalendar extends HTMLChakraProps<'div'> {
  startAt: string;
  endAt: string;
  days: Array<string>;
}

export const ScheduleCalendarEmbedded: FC<IScheduleCalendar> = ({ startAt, endAt, days, ...rest }) => {
  const { eventId, trackIds, day: activeDay, timeZone, view = 'day', change } = useQueryParams<IUrlQueryStringParams>({
    day: StringParam,
    view: StringParam,
    eventId: StringParam,
    trackIds: StringParam,
    timeZone: StringParam,
  });

  const theme = useTheme();
  const eventDefaultColors = {
    textColor: theme.colors.white,
    backgroundColor: theme.colors.violet[500],
  };
  const calendarRef = useRef<FullCalendar>();

  const { data: event } = useResource(queryThinEvent(eventId));
  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(eventId)
  );

  const { data: eventSessions, mutate: mutateSession } = useResources(
    startAt &&
      endAt &&
      queryThinEventSessions({ event_id: eventId, day_starts_at: startAt, day_ends_at: endAt, track_id: trackIds })
  );

  useResources(queryAllTracks({ event_id: eventId }), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const tracksGroups = useMemo(() => getTracksGroups(eventSessions), [eventSessions]);

  const isNoTracks = useMemo(() => Object.keys(tracksGroups).length === 0, [tracksGroups]);
  const isPreviousDateTrackExist = useMemo(
    () =>
      !isNoTracks &&
      timeZone &&
      Object.keys(tracksGroups).some((item) => !isSameDay(utcToZonedTime(item, timeZone), toDate(activeDay))),
    [tracksGroups, activeDay, timeZone]
  );
  const firstEvent = useMemo(() => timeZone && getFirstEventByDate(tracksGroups, activeDay, timeZone), [
    tracksGroups,
    activeDay,
    timeZone,
  ]);
  const firstEventTime = useMemo(
    () =>
      !timeZone || isPreviousDateTrackExist || !firstEvent || view === 'week'
        ? '00:00:00'
        : utcToZonedTime(firstEvent, timeZone).toLocaleTimeString('en-GB'),
    [firstEvent, timeZone, isPreviousDateTrackExist]
  );
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  useEffect(() => {
    if (calendarRef?.current?.getApi && activeDay && firstEventTime) {
      const calendarApi = calendarRef.current.getApi();

      calendarApi?.setOption('slotMinTime', firstEventTime);
      calendarApi.refetchEvents();
      calendarApi?.gotoDate(activeDay);
    }
  }, [days, activeDay, firstEventTime, timeZone]);
  useEffect(() => {
    if (calendarRef?.current?.getApi && view) {
      const calendarApi = calendarRef.current.getApi();
      const initialView = view === 'week' ? 'timeGridWeek' : 'timeGridDay';

      calendarApi.changeView(initialView);
    }
  }, [view]);

  const initialView = view === 'week' ? 'timeGridWeek' : 'timeGridDay';
  const eventMaxStack = view === 'day' && isDesktop ? 5 : 2;

  const isSimplified = eventConfiguration.embeddingScheduleType === EmbeddingScheduleType.SIMPLIFIED;

  if (!activeDay || !event || !eventSessions || !view || !timeZone) {
    return (
      <VStack h="100%" w="100%" {...rest}>
        <Skeleton h="60px" w="25%" bg="#7d80804d" mt={6} mb={10} />
        <HStack h="10%" w="100%">
          <Skeleton h="50px" w="25%" bg="#7d80804d" />
          <Skeleton h="50px" w="25%" bg="#7d80804d" />
        </HStack>
        {[...Array(10)].map((_, index) => (
          <Skeleton key={index} h="10%" w="100%" mt={8} bg="#7d80804d" />
        ))}
      </VStack>
    );
  }

  return (
    <Stack pl={6} pr={14} mt={4} className={styles.calendar} {...rest}>
      {isNoTracks ? (
        <ScheduleCalendarNoTracks />
      ) : (
        <FullCalendar
          ref={calendarRef}
          locale="en-GB"
          now={() => new Date(new Date().toLocaleString('en-US', { timeZone })).toISOString()}
          events={getNormalizedEvents(tracksGroups, eventId, timeZone, eventDefaultColors, !isSimplified)}
          plugins={[timeGridPlugin]}
          allDaySlot={false}
          initialDate={activeDay}
          initialView={initialView}
          slotMinTime={firstEventTime}
          slotDuration="01:00:00"
          eventOverlap={false}
          nowIndicator
          eventContent={(eventData) => (
            <ScheduleCalendarTrackEmbedded
              mutateSession={mutateSession}
              trackData={eventData}
              timeZone={timeZone}
              view={view}
              activeDay={activeDay}
            />
          )}
          eventMaxStack={eventMaxStack}
          contentHeight="auto"
          headerToolbar={false}
          moreLinkContent={(data) => (
            <Box w="fit-content" h="fit-content">
              {data.shortText}
            </Box>
          )}
          eventTimeFormat={timeFormat}
          slotLabelFormat={timeFormat}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          slotEventOverlap={false}
          eventOrder="start,title,-duration,allDay"
        />
      )}
    </Stack>
  );
};
